import React from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { colors } from '../theme/colors';

/**
 * DocListPagination — page bar for the paginated document lists.
 * Render with the `pagination` object from useDocListPagination:
 *   <DocListPagination {...pagination} />
 * Renders nothing while a search/filter is active (full mode) or when
 * everything fits on one page.
 */
const DocListPagination = ({ page, totalPages, totalCount, goToPage, visible }) => {
  if (!visible) return null;

  // Window of page numbers around the current page: 1 … 4 5 [6] 7 8 … 20
  const numbers = [];
  const push = (n) => { if (!numbers.includes(n)) numbers.push(n); };
  push(1);
  for (let n = page - 2; n <= page + 2; n += 1) {
    if (n > 1 && n < totalPages) push(n);
  }
  if (totalPages > 1) push(totalPages);
  numbers.sort((a, b) => a - b);

  const items = [];
  let prev = 0;
  numbers.forEach(n => {
    if (n - prev > 1) items.push({ gap: true, key: `gap-${prev}` });
    items.push({ n, key: `p-${n}` });
    prev = n;
  });

  return (
    <Bar>
      <NavBtn disabled={page <= 1} onClick={() => goToPage(page - 1)} title="Previous page">
        <FiChevronLeft size={14} />
      </NavBtn>
      {items.map(item => item.gap
        ? <Gap key={item.key}>…</Gap>
        : (
          <PageBtn
            key={item.key}
            $active={item.n === page}
            onClick={() => item.n !== page && goToPage(item.n)}
          >
            {item.n}
          </PageBtn>
        ))}
      <NavBtn disabled={page >= totalPages} onClick={() => goToPage(page + 1)} title="Next page">
        <FiChevronRight size={14} />
      </NavBtn>
      <CountNote>{totalCount} document(s)</CountNote>
    </Bar>
  );
};

export default DocListPagination;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 0 2px;
  flex-wrap: wrap;
`;
const baseBtn = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;
const PageBtn = styled.button`
  ${baseBtn}
  border: 1px solid ${p => p.$active ? colors.primary.main : (colors.neutral?.[300] || '#d1d5db')};
  background: ${p => p.$active ? colors.primary.main : 'white'};
  color: ${p => p.$active ? 'white' : colors.text.primary};
  cursor: ${p => p.$active ? 'default' : 'pointer'};
  &:hover { ${p => p.$active ? '' : `border-color: ${colors.primary.main}; color: ${colors.primary.main};`} }
`;
const NavBtn = styled.button`
  ${baseBtn}
  border: 1px solid ${colors.neutral?.[300] || '#d1d5db'};
  background: white;
  color: ${colors.text.primary};
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:not(:disabled):hover { border-color: ${colors.primary.main}; color: ${colors.primary.main}; }
`;
const Gap = styled.span`
  padding: 0 2px;
  color: ${colors.text.secondary};
  font-size: 12px;
`;
const CountNote = styled.span`
  margin-left: 10px;
  font-size: 11px;
  color: ${colors.text.secondary};
`;
